import { MYDW_LOGO } from "components/blocks/sidenav/TopInfo";
import * as s from "styles/ModalCommonStyled";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import SelectComponent from "components/atoms/select/SelectComponent";
import Toggle from "components/atoms/toggle/Toggle";
import * as option from "utils/options";
import { EditCompanyLogo } from "../AccountStyled";
import TextArea from "components/atoms/input/TextArea";
import { HiUpload } from "react-icons/hi";
import TinyButton from "components/atoms/buttons/TinyButton";
import Spinner from "components/atoms/loader/Spinner";
import AdditionalAccountIdModalInput from "components/atoms/input/modal/AdditionalAccountIdModalInput";
import { UPDATE_TYPE } from "./AccountDetailContainer";
import {
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { Option } from "utils/options";
import AddressInput from "components/atoms/input/address/AddressInput";
import { changeLevelFromABBToABB, checkEmpty, toTrim } from "utils/functions";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { orgNameAlphabetRule, orgNameRule, orgNameRules } from "utils/regEx";
import React from "react";
import { useAppSelector } from "redux/hooks";
import {
  AccountInfo,
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { BILLING_TYPE } from "api/interfaces/planInterface.interface";
import useApiError from "hook/useApiError";
import OrgNameBubble from "components/blocks/bubble/OrgNameBubble";
import { PasswordBubble } from "pages/auth/signup/SignupStyled";
import {
  getExpiredFromTrialAndChannel,
  getIsBillingTypeDisabled,
} from "utils/AccountUtil";
import { IoAlertCircle } from "react-icons/io5";
import { calcRem } from "styles/theme";
import { USAGE_PLAN_SWITCH_WARNING_TEXT } from "utils/SubscriptionUtil";
import { useFlag } from "@unleash/proxy-client-react";

export interface Props {
  accountInfo?: AccountInfo;
  editAccountInfo: AccountInnerInfo;
  onChangeAccountInfo: (
    type: UPDATE_TYPE | number,
    value: number | string | boolean
  ) => void;
  logoHandleChange?: (e: any) => void;
  logoImage?: string;
  isUpdateLoading?: boolean;
  validate?: Validate;
  validateMsg: ValidateMsg;
  setValidateMsg?: React.Dispatch<React.SetStateAction<ValidateMsg>>;
  accountAdmins?: Option[];
  accountOwnerIsFetching?: boolean;
  isMyAccount?: boolean;
  uploadingLogo?: boolean;
  isAdd?: boolean;
}

interface AccountInnerInfo {
  name: string;
  level: LEVEL_TYPE;
  accountNumber: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  accountAdditionalNumber: string;
  use2faAuth: boolean;
  ownerId?: string;
  companyName?: string;
  companyLocation?: string;
  description?: string;
  billingType?: BILLING_TYPE;
}

export type AccountNameValidationError = "name" | "nameInvalid";

export function getAccountNameValidationError(
  name: string
): AccountNameValidationError | null {
  const trimmed = toTrim(name);
  if (checkEmpty(trimmed)) return "name";
  if (
    trimmed.length < 3 ||
    trimmed.length > 100 ||
    orgNameRule(trimmed) ||
    orgNameAlphabetRule(trimmed)
  ) {
    return "nameInvalid";
  }
  return null;
}
export function AccountInner(props: Props): JSX.Element {
  const {
    accountInfo,
    editAccountInfo,
    onChangeAccountInfo,
    logoHandleChange,
    logoImage,
    isUpdateLoading = false,
    validate,
    validateMsg,
    setValidateMsg,
    accountAdmins = [],
    accountOwnerIsFetching = false,
    isMyAccount,
    uploadingLogo = false,
    isAdd = false,
  } = props;

  const { lang, intlString } = useApiError();
  const {
    accountLevel,
    parentLevel,
    isAdminGroup,
    isUsagePaid,
  }: ProfileAccountInfo = useAppSelector((state) => state.accountSettings);
  const addBaseLevel = changeLevelFromABBToABB({
    level: accountLevel as LEVEL_TYPE,
    parentLevel: parentLevel as LEVEL_TYPE,
  });
  const levelOptions = isAdd
    ? option.accountLevelOptions(addBaseLevel)
    : option.accountLevelOptionsAll;
  const isLevelDisabled = isAdd ? addBaseLevel !== LEVEL_TYPE.CP : true;
  const showTwoFactorAuth = isAdd ? isAdminGroup : true;

  const nameValidateMsg = validateMsg.name;
  const isNameValidated = validate ? validate.name : validateMsg?.name !== "";
  const levelValidateMsg = isAdd ? validateMsg.isLevel : "";
  const isLevelValidated = isAdd ? validate?.isLevel || false : false;

  const [isNameFocus, setIsNameFocus] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const isPlanHide = useFlag("feature_plan_hide");
  const isBillingTypeDisabled = getIsBillingTypeDisabled({
    isUsagePaid,
    isAdd,
    accountInfo,
  });

  const onBlurName = useCallback(() => {
    if (!setValidateMsg) return;
    const error = getAccountNameValidationError(editAccountInfo.name || "");
    if (!error) return;
    setValidateMsg((msg) => ({
      ...msg,
      name: intlString(lang.validateMsg.account[error]),
    }));
  }, [editAccountInfo.name, setValidateMsg, intlString, lang]);

  useEffect(() => {
    const value = toTrim(editAccountInfo.name);
    if (
      isNameFocus &&
      !(value.length < 3 || value.length > 100) &&
      !orgNameRule(value) &&
      !orgNameAlphabetRule(value)
    ) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setIsNameFocus(false);
        onBlurName();
      }, 800);
      return () => clearTimeout(timerRef.current as NodeJS.Timeout);
    }
  }, [editAccountInfo.name, isNameFocus, onBlurName]);

  return (
    <s.EditWrapper className="account-detail-wrapper">
      {(!accountInfo || isUpdateLoading) && (
        <s.LoadingWrapper>
          <Spinner isCenter />
        </s.LoadingWrapper>
      )}

      <s.Row>
        <ModalInput
          label="Name"
          essential
          flex={isMyAccount ? 1 : 3}
          validatedWord={nameValidateMsg}
          isValidated={isNameValidated}
          half={isMyAccount}
        >
          <NameInputWrapper>
            <Input
              id="dw_account_input_name"
              className="input-field"
              placeholder="Please enter a name"
              autoFocus={isAdd}
              value={editAccountInfo.name}
              maxLength={100}
              onChange={(e) =>
                onChangeAccountInfo(UPDATE_TYPE.NAME, e.target.value)
              }
              onFocus={() => setIsNameFocus(true)}
              onBlur={() => setIsNameFocus(false)}
              isValidated={isNameValidated}
              data-testid="account-detail-input-name"
            />

            {isNameFocus && (
              <PasswordBubble>
                <OrgNameBubble password={toTrim(editAccountInfo.name)} />
              </PasswordBubble>
            )}
          </NameInputWrapper>
        </ModalInput>
        {isMyAccount ? (
          <ModalInput label="Timezone" flex={1}>
            <SelectComponent
              id="dw_account_input_timezone"
              value={editAccountInfo.timezone}
              options={option.timezoneOptions}
              onChange={(value) =>
                onChangeAccountInfo(UPDATE_TYPE.TIMEZONE, value)
              }
              data-testid="account-detail-input-timezone"
            />
          </ModalInput>
        ) : (
          <ModalInput
            label="Level"
            essential
            flex={1}
            validatedWord={levelValidateMsg}
            isValidated={isLevelValidated}
          >
            <SelectComponent
              id="dw_account_input_level"
              disabled={isLevelDisabled}
              options={levelOptions}
              value={editAccountInfo.level}
              onChange={(value) =>
                onChangeAccountInfo(UPDATE_TYPE.LEVEL, value)
              }
              data-testid="account-detail-input-level"
            />
          </ModalInput>
        )}
      </s.Row>

      {!isMyAccount && (
        <ModalInput label="Timezone">
          <SelectComponent
            id="dw_account_input_timezone"
            value={option.timezoneOptions.filter(function (option) {
              return option.value === editAccountInfo.timezone;
            })}
            options={option.timezoneOptions}
            onChange={(value) =>
              onChangeAccountInfo(UPDATE_TYPE.TIMEZONE, value)
            }
            data-testid="account-detail-input-timezone-2"
          />
        </ModalInput>
      )}

      <s.Row>
        <ModalInput label="Date Format">
          <SelectComponent
            id="dw_account_input_dataformat"
            value={option.dateFormatOptions.filter(function (option) {
              return option.value === editAccountInfo.dateFormat;
            })}
            options={option.dateFormatOptions}
            onChange={(value) =>
              onChangeAccountInfo(UPDATE_TYPE.DATE_FORMAT, value)
            }
            data-testid="account-detail-input-dataformat"
          />
        </ModalInput>
        <ModalInput label="Time Format">
          <SelectComponent
            id="dw_account_input_timeformat"
            value={option.timeFormatOptions.filter(function (option) {
              return option.value === editAccountInfo.timeFormat;
            })}
            options={option.timeFormatOptions}
            onChange={(value) =>
              onChangeAccountInfo(UPDATE_TYPE.TIME_FORMAT, value)
            }
            data-testid="account-detail-input-timeformat"
          />
        </ModalInput>
      </s.Row>

      {!isPlanHide &&
        !isMyAccount &&
        addBaseLevel === LEVEL_TYPE.CP &&
        editAccountInfo.level === LEVEL_TYPE.EU && (
          <ModalInput label="Billing Type">
            <SelectComponent
              disabled={isBillingTypeDisabled}
              id="dw_account_input_billingType"
              value={editAccountInfo?.billingType}
              options={option.billingType}
              onChange={(value) =>
                onChangeAccountInfo(UPDATE_TYPE.BILLING_TYPE, value)
              }
              data-testid="account-detail-input-billing-type"
            />
            {isUsagePaid && isBillingTypeDisabled && (
              <BillingTypeWarning>
                <IoAlertCircle size={16} />
                <span>{USAGE_PLAN_SWITCH_WARNING_TEXT}</span>
              </BillingTypeWarning>
            )}
          </ModalInput>
        )}
      {isMyAccount && (
        <s.Row>
          <ModalInput label="Company Name">
            <Input
              id="dw_account_input_companyName"
              value={editAccountInfo.companyName}
              placeholder="Please enter a company name"
              onChange={(e) =>
                onChangeAccountInfo(UPDATE_TYPE.COMPANY_NAME, e.target.value)
              }
              data-testid="account-detail-input-company-name"
            />
          </ModalInput>
          <ModalInput label="Company Location">
            <AddressInput
              isLoaded={true}
              value={editAccountInfo.companyLocation}
              onChange={(e) =>
                onChangeAccountInfo(
                  UPDATE_TYPE.COMPANY_LOCATION,
                  e.currentTarget.value
                )
              }
              onChangeLocationSelect={(
                address: google.maps.LatLngLiteral,
                addressString?: string
              ) => {
                if (addressString !== undefined) {
                  onChangeAccountInfo(
                    UPDATE_TYPE.COMPANY_LOCATION,
                    addressString
                  );
                }
              }}
              data-testid="account-detail-address-input"
            />
          </ModalInput>
        </s.Row>
      )}

      <s.EditAccountCompany>
        <div style={isAdd ? { width: "100%" } : undefined}>
          <ModalInput label="Organization ID">
            <Input
              id="dw_account_input_accountNumber"
              disabled
              placeholder=""
              value={editAccountInfo.accountNumber}
              data-testid="account-detail-input-account-number-change"
            />
          </ModalInput>
          <AdditionalAccountIdModalInput>
            <Input
              id="dw_account_input_accountNumberAdditional"
              placeholder="Enter an additional organization number"
              value={editAccountInfo.accountAdditionalNumber}
              maxLength={100}
              onChange={(e) =>
                onChangeAccountInfo(
                  UPDATE_TYPE.ADDITIONAL_ACCOUNT_NUMBER,
                  e.target.value
                )
              }
              data-testid="account-detail-input-account-number-additional"
            />
          </AdditionalAccountIdModalInput>
          {isMyAccount && (
            <ModalInput label="Owner" noBold>
              <SelectComponent
                disabled={accountAdmins.length === 1}
                loading={accountOwnerIsFetching}
                id="dw_account_input_owner"
                placeholder={`${accountInfo?.ownerName} (${accountInfo?.ownerEmail})`}
                value={editAccountInfo?.ownerId}
                options={accountAdmins}
                onChange={(value) =>
                  onChangeAccountInfo(UPDATE_TYPE.OWNER, value)
                }
                data-testid="account-detail-input-owner"
              />
            </ModalInput>
          )}

          {showTwoFactorAuth && (
            <s.ToggleContainer>
              Two-Factor Authentication (2FA)
              <Toggle
                id="dw_account_input_2fa"
                isToggled={editAccountInfo.use2faAuth}
                checked={editAccountInfo.use2faAuth}
                onChange={(checked: boolean) =>
                  onChangeAccountInfo(UPDATE_TYPE.TOW_FACTOR_AUTH, checked)
                }
                dataTestId="account-detail-input-2fa"
              />
            </s.ToggleContainer>
          )}
        </div>

        {!isAdd && (
          <s.EditLeftWrapper>
            <ModalInput label="Company Logo">
              <EditCompanyLogo className="company-logo">
                <TinyButton
                  label="Change"
                  buttonType="cancel"
                  onClickBtn={() =>
                    document.getElementById("logoUPload")?.click()
                  }
                  icon={<HiUpload />}
                  data-testid="account-detail-tiny-button-get-element-by-id"
                />

                <input
                  type="file"
                  id="logoUPload"
                  accept={option.enableImageTypesOption.join(", ")}
                  style={{ display: "none" }}
                  onChange={logoHandleChange}
                  data-testid="account-detail-logo-upload-logo-handle-change"
                />

                {logoImage !== "" && logoImage !== undefined && (
                  <img
                    src={
                      logoImage === "" || logoImage === undefined
                        ? MYDW_LOGO
                        : logoImage
                    }
                    alt="company logo"
                  />
                )}

                {uploadingLogo && <Spinner />}
              </EditCompanyLogo>
            </ModalInput>
          </s.EditLeftWrapper>
        )}
      </s.EditAccountCompany>

      {!isAdd && (
        <ModalInput label="Description">
          <TextArea
            placeholder="Please enter a description"
            value={editAccountInfo.description}
            maxLength={200}
            onChange={(e) =>
              onChangeAccountInfo(UPDATE_TYPE.DESC, e.target.value)
            }
            data-testid="account-detail-textarea"
          />
        </ModalInput>
      )}
    </s.EditWrapper>
  );
}

const NameInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const BillingTypeWarning = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.COLORS.WARNING};
  background-color: ${({ theme }) => theme.COLORS.WARNING}1A;
  color: ${({ theme }) => theme.COLORS.WARNING};
  font-size: ${calcRem(13)};
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;
